import {
  Alert,
  Box,
  Button,
  GridedContent,
  GridedLayout,
  GridedMain,
  Header,
  IconCheckCircle,
  IconCheckCircleOutline,
  IconLaunch,
  IconLink,
  Input,
  Spacer,
  Typography,
  useLayout,
} from '@capitalise/ui-design-system';
import { useModalContext } from 'hooks/useModalContext';
// @ts-ignore
import CapitaliseLogoRefer from 'assets/svgs/capitalise-logo-refer.svg';
import useToggle from 'hooks/useToggle';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import { useState } from 'react';
import RadioOptionsGroups from '../RadioOptionsGroup';
import { partnerReferralTypes } from 'enums/partner-referral-types';

interface IReferInviteProps {
  introducerToken: string;
}

const ReferInvite = ({ introducerToken }: IReferInviteProps) => {
  const { close } = useModalContext();
  const { renderToaster } = useLayout();
  const [showCopyButton, toggleShowCopyButton] = useToggle(true);
  const [showRadios, toggleShowRadios] = useToggle(true);

  const [partnerReferral, setPartnerReferral] = useState(partnerReferralTypes.revenueShare);

  // @ts-ignore
  const inputValue = `${window._env_.SPA_DOMAIN}/signup/business?introducer=${introducerToken}&partnerReferral=${partnerReferral}`;

  const onCopyLinkClick = () => {
    Analytics.track(AnalyticsEventCategory.referral, AnalyticsEventAction.shareCopyLink);

    toggleShowCopyButton();
    copy();
    setTimeout(() => {
      toggleShowCopyButton();
    }, 4000);
  };

  const copy = (): void => {
    navigator.clipboard.writeText(inputValue);
    renderToaster(
      <Spacer gap="tiny" alignItems="center">
        <IconCheckCircle color="neutral-white" />
        <Typography size="body-14" color="white">
          Link copied to clipboard
        </Typography>
      </Spacer>,
    );
  };

  const openInNewTab = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box backgroundColor="white">
      <GridedLayout>
        <Header
          actions={
            <Button variant="outlined" baseColor="green" onClick={close}>
              Close
            </Button>
          }
          isPoweredByExperian
        />
        <GridedMain>
          <GridedContent>
            <Box
              gridColumn={{ _: '1/5', sm: '3/7', lg: '5/9' }}
              display="flex"
              flexDirection="column"
              py="xsmall"
            >
              <Box display="flex" justifyContent="center">
                <CapitaliseLogoRefer />
              </Box>
              <Typography size="heading-32" color="grey-900" pt="3xsmall">
                Invite your client to access tools from Capitalise
              </Typography>
              <Typography size="body-18" color="grey-700" pt="tiny">
                Your clients will be able to check their credit score, apply for funding, and more.
              </Typography>
              <Box width="100%" pt="xsmall">
                <Alert
                  type="info"
                  actionLabel="Learn more"
                  actionIcon={{ left: <IconLaunch /> }}
                  onActionClick={() =>
                    openInNewTab('https://capitalise.com/gb/advisers/features/client-tools')
                  }
                >
                  <Typography size="body-16" color="grey-900">
                    What will my client see?
                  </Typography>
                </Alert>
              </Box>
              <Box display="flex" width="100%" pt="xsmall">
                <RadioOptionsGroups
                  partnerReferral={partnerReferral}
                  setPartnerReferral={setPartnerReferral}
                  copyLinkCallback={onCopyLinkClick}
                  showRadios={showRadios}
                  toggleShowRadios={toggleShowRadios}
                />
              </Box>
              {!showRadios && (
                <Box display="flex" width="100%" pt="xsmall" gridGap="3xsmall">
                  <Box flex="1">
                    <Input
                      label=""
                      showLabel={false}
                      defaultValue={inputValue}
                      readOnly
                      fullWidth
                    />
                  </Box>
                  {showCopyButton ? (
                    <Button onClick={onCopyLinkClick} baseColor="green" leftIcon={<IconLink />}>
                      Copy link
                    </Button>
                  ) : (
                    <Button baseColor="green" leftIcon={<IconCheckCircleOutline />}>
                      Link copied
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </GridedContent>
        </GridedMain>
      </GridedLayout>
    </Box>
  );
};

export default ReferInvite;
